// src/utils/dataFetcher.js
import { db } from '../firebase/config';
import { collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

/**
 * Converts a time period string to a date range
 * @param {string} timePeriod - The time period (today, yesterday, this_week, etc.)
 * @returns {Object} Object with start and end dates
 */
const getDateRangeForTimePeriod = (timePeriod) => {
  const now = new Date();
  let startDate, endDate;
  
  switch(timePeriod) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      break;
    case 'yesterday':
      startDate = subDays(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0), 1);
      endDate = subDays(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999), 1);
      break;
    case 'this_week':
      startDate = startOfWeek(now);
      endDate = endOfWeek(now);
      break;
    case 'last_week':
      startDate = startOfWeek(subDays(now, 7));
      endDate = endOfWeek(subDays(now, 7));
      break;
    case 'this_month':
      startDate = startOfMonth(now);
      endDate = endOfMonth(now);
      break;
    case 'last_month':
      startDate = startOfMonth(subDays(now, 30));
      endDate = endOfMonth(subDays(now, 30));
      break;
    case 'this_quarter':
      const currentMonth = now.getMonth();
      const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
      startDate = new Date(now.getFullYear(), quarterStartMonth, 1);
      endDate = new Date(now.getFullYear(), quarterStartMonth + 3, 0, 23, 59, 59, 999);
      break;
    case 'this_year':
      startDate = startOfYear(now);
      endDate = endOfYear(now);
      break;
    case 'last_year':
      startDate = startOfYear(subDays(now, 365));
      endDate = endOfYear(subDays(now, 365));
      break;
    case 'all_time':
      startDate = new Date(2000, 0, 1);
      endDate = new Date(now.getFullYear() + 10, 11, 31, 23, 59, 59, 999);
      break;
    default:
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  }
  
  // Convert to Firestore timestamps
  return { 
    startDate: Timestamp.fromDate(startDate), 
    endDate: Timestamp.fromDate(endDate)
  };
};

/**
 * Fetches sales data from Firestore
 * @param {string} timePeriod - The time period to fetch sales for
 * @param {Object} userProfile - User profile containing business info
 * @returns {Promise<Array>} Array of sales records
 */
const fetchSales = async (timePeriod, userProfile, activeBusiness) => {
  try {
    const { startDate, endDate } = getDateRangeForTimePeriod(timePeriod);

    const userId = userProfile?.id;
    const businessId = activeBusiness?.id || userProfile?.businessId;
    if (!userId) {
      return { error: "No user ID found in your profile" };
    }

    const salesRef = collection(db, "sales");
    const constraints = [
      where("createdBy", "==", userId),
      where("date", ">=", startDate),
      where("date", "<=", endDate),
      orderBy("date", "desc"),
      limit(50)
    ];
    if (businessId) {
      constraints.splice(1, 0, where("businessId", "==", businessId));
    }
    const q = query(salesRef, ...constraints);
    
    const querySnapshot = await getDocs(q);
    const sales = [];
    
    querySnapshot.forEach((doc) => {
      sales.push({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate ? doc.data().date.toDate() : doc.data().date
      });
    });
    
    return sales;
  } catch (error) {
    console.error("Error fetching sales:", error);
    return { error: error.message };
  }
};

/**
 * Fetches inventory data from Firestore
 * @param {Object} userProfile - User profile containing business info
 * @returns {Promise<Array>} Array of inventory items
 */
const fetchInventory = async (userProfile, activeBusiness) => {
  try {
    const userId = userProfile?.id;
    const businessId = activeBusiness?.id || userProfile?.businessId;
    if (!userId) {
      return { error: "No user ID found in your profile" };
    }

    const itemsRef = collection(db, "stockItems");
    const constraints = [
      where("createdBy", "==", userId),
      limit(100)
    ];
    if (businessId) {
      constraints.splice(1, 0, where("businessId", "==", businessId));
    }
    const q = query(itemsRef, ...constraints);
    
    const querySnapshot = await getDocs(q);
    const items = [];
    
    querySnapshot.forEach((doc) => {
      items.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return items;
  } catch (error) {
    console.error("Error fetching inventory items:", error);
    return { error: error.message };
  }
};

/**
 * Fetches customer data from Firestore
 * @param {Object} userProfile - User profile containing business info
 * @returns {Promise<Array>} Array of customers
 */
const fetchCustomers = async (userProfile, activeBusiness) => {
  try {
    const userId = userProfile?.id;
    const businessId = activeBusiness?.id || userProfile?.businessId;
    if (!userId) {
      return { error: "No user ID found in your profile" };
    }

    const customersRef = collection(db, "customers");
    const constraints = [
      where("createdBy", "==", userId),
      limit(100)
    ];
    if (businessId) {
      constraints.splice(1, 0, where("businessId", "==", businessId));
    }
    const q = query(customersRef, ...constraints);
    
    const querySnapshot = await getDocs(q);
    const customers = [];
    
    querySnapshot.forEach((doc) => {
      customers.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return customers;
  } catch (error) {
    console.error("Error fetching customers:", error);
    return { error: error.message };
  }
};

/**
 * Fetches financial transactions from Firestore
 * @param {string} timePeriod - The time period to fetch transactions for
 * @param {Object} userProfile - User profile containing business info
 * @returns {Promise<Array>} Array of financial transactions
 */
const fetchFinances = async (timePeriod, userProfile, activeBusiness) => {
  try {
    const { startDate, endDate } = getDateRangeForTimePeriod(timePeriod);

    const userId = userProfile?.id;
    const businessId = activeBusiness?.id || userProfile?.businessId;
    if (!userId) {
      return { error: "No user ID found in your profile" };
    }

    const transactionsRef = collection(db, "financialTransactions");
    const constraints = [
      where("createdBy", "==", userId),
      where("date", ">=", startDate),
      where("date", "<=", endDate),
      orderBy("date", "desc"),
      limit(50)
    ];
    if (businessId) {
      constraints.splice(1, 0, where("businessId", "==", businessId));
    }
    const q = query(transactionsRef, ...constraints);
    
    const querySnapshot = await getDocs(q);
    const transactions = [];
    
    querySnapshot.forEach((doc) => {
      transactions.push({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate ? doc.data().date.toDate() : doc.data().date
      });
    });
    
    return transactions;
  } catch (error) {
    console.error("Error fetching financial transactions:", error);
    return { error: error.message };
  }
};

/**
 * Fetches orders from Firestore
 * @param {string} timePeriod - The time period to fetch orders for
 * @param {Object} userProfile - User profile containing business info
 * @returns {Promise<Array>} Array of orders
 */
const fetchOrders = async (timePeriod, userProfile, activeBusiness) => {
  try {
    const { startDate, endDate } = getDateRangeForTimePeriod(timePeriod);

    const userId = userProfile?.id;
    const businessId = activeBusiness?.id || userProfile?.businessId;
    if (!userId) {
      return { error: "No user ID found in your profile" };
    }

    const ordersRef = collection(db, "orders");
    const constraints = [
      where("createdBy", "==", userId),
      where("createdAt", ">=", startDate),
      where("createdAt", "<=", endDate),
      orderBy("createdAt", "desc"),
      limit(50)
    ];
    if (businessId) {
      constraints.splice(1, 0, where("businessId", "==", businessId));
    }
    const q = query(ordersRef, ...constraints);
    
    const querySnapshot = await getDocs(q);
    const orders = [];
    
    querySnapshot.forEach((doc) => {
      orders.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt
      });
    });
    
    return orders;
  } catch (error) {
    console.error("Error fetching orders:", error);
    return { error: error.message };
  }
};

/**
 * Fetches employee data from Firestore
 * @param {Object} userProfile - User profile containing business info
 * @returns {Promise<Array>} Array of employees
 */
const fetchEmployees = async (userProfile, activeBusiness) => {
  try {
    const userId = userProfile?.id;
    const businessId = activeBusiness?.id || userProfile?.businessId;
    if (!userId) {
      return { error: "No user ID found in your profile" };
    }

    const employeesRef = collection(db, "employees");
    const constraints = [
      where("createdBy", "==", userId),
      limit(100)
    ];
    if (businessId) {
      constraints.splice(1, 0, where("businessId", "==", businessId));
    }
    const q = query(employeesRef, ...constraints);
    
    const querySnapshot = await getDocs(q);
    const employees = [];
    
    querySnapshot.forEach((doc) => {
      employees.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return employees;
  } catch (error) {
    console.error("Error fetching employees:", error);
    return { error: error.message };
  }
};

/**
 * Fetches marketing data from Firestore
 * @param {string} timePeriod - The time period to fetch marketing data for
 * @param {Object} userProfile - User profile containing business info
 * @returns {Promise<Array>} Array of marketing campaigns/analytics
 */
const fetchMarketing = async (timePeriod, userProfile, activeBusiness) => {
  try {
    const { startDate, endDate } = getDateRangeForTimePeriod(timePeriod);

    const userId = userProfile?.id;
    const businessId = activeBusiness?.id || userProfile?.businessId;
    if (!userId) {
      return { error: "No user ID found in your profile" };
    }

    const marketingRef = collection(db, "marketing");
    const constraints = [
      where("createdBy", "==", userId),
      where("date", ">=", startDate),
      where("date", "<=", endDate),
      orderBy("date", "desc"),
      limit(50)
    ];
    if (businessId) {
      constraints.splice(1, 0, where("businessId", "==", businessId));
    }
    const q = query(marketingRef, ...constraints);
    
    const querySnapshot = await getDocs(q);
    const marketing = [];
    
    querySnapshot.forEach((doc) => {
      marketing.push({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate ? doc.data().date.toDate() : doc.data().date
      });
    });
    
    return marketing;
  } catch (error) {
    console.error("Error fetching marketing data:", error);
    return { error: error.message };
  }
};

/**
 * Fetches business details from Firestore
 * @param {Object} userProfile - User profile containing business info
 * @returns {Promise<Object>} Business details
 */
const fetchBusinessDetails = async (userProfile, activeBusiness) => {
  try {
    // Guard against missing businessId
    const businessId = activeBusiness?.id || userProfile?.businessId;
    if (!businessId) {
      return { error: "No business ID found in your profile" };
    }

    const businessRef = collection(db, "businesses");
    const q = query(
      businessRef,
      where("id", "==", businessId),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    let business = null;
    
    querySnapshot.forEach((doc) => {
      business = {
        id: doc.id,
        ...doc.data()
      };
    });
    
    return business || { error: "Business not found" };
  } catch (error) {
    console.error("Error fetching business details:", error);
    return { error: error.message };
  }
};

/**
 * Fetches data based on selected sources and time period
 * @param {string} timePeriod - The time period to fetch data for
 * @param {Array} dataSources - Array of data source types to fetch
 * @param {Object} userProfile - User profile containing business info
 * @returns {Promise<Object>} Object containing data from all requested sources
 */
export const fetchContextData = async (timePeriod, dataSources, userProfile, activeBusiness) => {
  try {
    // Guard against missing userProfile
    if (!userProfile) {
      console.error("Error fetching context data: Missing user profile");
      return { error: "Missing user profile" };
    }

    const result = {};

    // Process each requested data source
    for (const source of dataSources) {
      switch (source) {
        case 'sales':
          result.sales = await fetchSales(timePeriod, userProfile, activeBusiness);
          break;
        case 'inventory':
          result.inventory = await fetchInventory(userProfile, activeBusiness);
          break;
        case 'customers':
          result.customers = await fetchCustomers(userProfile, activeBusiness);
          break;
        case 'finances':
          result.finances = await fetchFinances(timePeriod, userProfile, activeBusiness);
          break;
        case 'orders':
          result.orders = await fetchOrders(timePeriod, userProfile, activeBusiness);
          break;
        case 'employees':
          result.employees = await fetchEmployees(userProfile, activeBusiness);
          break;
        case 'marketing':
          result.marketing = await fetchMarketing(timePeriod, userProfile, activeBusiness);
          break;
        case 'business':
          result.business = await fetchBusinessDetails(userProfile, activeBusiness);
          break;
        default:
          console.warn(`Unknown data source: ${source}`);
      }
    }
    
    return result;
  } catch (error) {
    console.error("Error fetching context data:", error);
    return { error: "Failed to fetch context data: " + error.message };
  }
};

/**
 * Formats the fetched data into a human-readable format for the AI prompt
 * @param {Object} data - The data fetched from Firestore
 * @returns {string} Formatted data as a string
 */
export const formatContextData = (data) => {
  // Handle error case
  if (data.error) {
    return `ERROR: ${data.error}`;
  }
  
  // Handle empty data
  if (Object.keys(data).length === 0) {
    return "No data available for the requested time period and sources.";
  }
  
  let formattedOutput = [];
  
  // Format sales data if present
  if (data.sales) {
    if (Array.isArray(data.sales)) {
      let salesSection = `SALES (${data.sales.length} total):\n`;
      
      // Calculate totals
      const totalRevenue = data.sales.reduce((sum, sale) => sum + (sale.totalAmount || 0), 0);
      
      salesSection += `Total Revenue: ${formatCurrency(totalRevenue)}\n`;
      salesSection += `Number of Sales: ${data.sales.length}\n`;
      
      // Group sales by product category if available
      const salesByCategory = {};
      data.sales.forEach(sale => {
        if (sale.items) {
          sale.items.forEach(item => {
            const category = item.category || 'Uncategorized';
            if (!salesByCategory[category]) {
              salesByCategory[category] = 0;
            }
            salesByCategory[category] += (item.totalAmount || 0);
          });
        }
      });
      
      if (Object.keys(salesByCategory).length > 0) {
        salesSection += "\nSales by Category:\n";
        Object.entries(salesByCategory).forEach(([category, amount]) => {
          salesSection += `- ${category}: ${formatCurrency(amount)}\n`;
        });
      }
      
      // Add recent sales (limited to 5)
      salesSection += "\nRecent Sales:\n";
      data.sales.slice(0, 5).forEach(sale => {
        const date = sale.date instanceof Date 
          ? format(sale.date, 'yyyy-MM-dd')
          : 'Unknown date';
        
        salesSection += `- ${date}: ${formatCurrency(sale.totalAmount || 0)} (${sale.status || 'No status'})\n`;
      });
      
      formattedOutput.push(salesSection);
    } else if (data.sales.error) {
      formattedOutput.push(`SALES ERROR: ${data.sales.error}`);
    }
  }
  
  // Format inventory data if present
  if (data.inventory) {
    if (Array.isArray(data.inventory)) {
      let inventorySection = `INVENTORY (${data.inventory.length} total items):\n`;
      
      // Calculate totals and stats
      const totalItems = data.inventory.length;
      const totalValue = data.inventory.reduce((sum, item) => sum + ((item.costPrice || 0) * getTotalStock(item)), 0);
      
      // Find low stock items
      const lowStockItems = data.inventory.filter(item => {
        const totalStock = getTotalStock(item);
        return totalStock !== '∞' && totalStock <= (item.minStockLevel || 0);
      });
      
      inventorySection += `Total Inventory Value: ${formatCurrency(totalValue)}\n`;
      inventorySection += `Total Items: ${totalItems}\n`;
      inventorySection += `Low Stock Items: ${lowStockItems.length}\n`;
      
      // List low stock items
      if (lowStockItems.length > 0) {
        inventorySection += "\nLow Stock Items:\n";
        lowStockItems.slice(0, 5).forEach(item => {
          const stock = getTotalStock(item);
          inventorySection += `- ${item.name}: ${stock} in stock (min: ${item.minStockLevel || 0})\n`;
        });
      }
      
      // Group by category
      const itemsByCategory = {};
      data.inventory.forEach(item => {
        const category = item.category || 'Uncategorized';
        if (!itemsByCategory[category]) {
          itemsByCategory[category] = 0;
        }
        itemsByCategory[category]++;
      });
      
      if (Object.keys(itemsByCategory).length > 0) {
        inventorySection += "\nItems by Category:\n";
        Object.entries(itemsByCategory).forEach(([category, count]) => {
          inventorySection += `- ${category}: ${count} items\n`;
        });
      }
      
      formattedOutput.push(inventorySection);
    } else if (data.inventory.error) {
      formattedOutput.push(`INVENTORY ERROR: ${data.inventory.error}`);
    }
  }
  
  // Format customer data if present
  if (data.customers) {
    if (Array.isArray(data.customers)) {
      let customersSection = `CUSTOMERS (${data.customers.length} total):\n`;
      
      // Get active vs inactive
      const activeCustomers = data.customers.filter(customer => customer.status === 'active').length;
      
      customersSection += `Total Customers: ${data.customers.length}\n`;
      customersSection += `Active Customers: ${activeCustomers}\n`;
      customersSection += `Inactive Customers: ${data.customers.length - activeCustomers}\n`;
      
      // Customer segmentation if available
      const customerSegments = {};
      data.customers.forEach(customer => {
        const segment = customer.segment || 'Not segmented';
        if (!customerSegments[segment]) {
          customerSegments[segment] = 0;
        }
        customerSegments[segment]++;
      });
      
      if (Object.keys(customerSegments).length > 0) {
        customersSection += "\nCustomer Segments:\n";
        Object.entries(customerSegments).forEach(([segment, count]) => {
          customersSection += `- ${segment}: ${count} customers\n`;
        });
      }
      
      formattedOutput.push(customersSection);
    } else if (data.customers.error) {
      formattedOutput.push(`CUSTOMERS ERROR: ${data.customers.error}`);
    }
  }
  
  // Format finance data if present
  if (data.finances) {
    if (Array.isArray(data.finances)) {
      let financesSection = `FINANCES (${data.finances.length} transactions):\n`;
      
      // Calculate income and expenses
      const income = data.finances
        .filter(tx => tx.amount > 0)
        .reduce((sum, tx) => sum + tx.amount, 0);
      
      const expenses = data.finances
        .filter(tx => tx.amount < 0)
        .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
      
      financesSection += `Total Income: ${formatCurrency(income)}\n`;
      financesSection += `Total Expenses: ${formatCurrency(expenses)}\n`;
      financesSection += `Net Cash Flow: ${formatCurrency(income - expenses)}\n`;
      
      // Group by category
      const expensesByCategory = {};
      data.finances
        .filter(tx => tx.amount < 0 && tx.category)
        .forEach(tx => {
          if (!expensesByCategory[tx.category]) {
            expensesByCategory[tx.category] = 0;
          }
          expensesByCategory[tx.category] += Math.abs(tx.amount);
        });
      
      if (Object.keys(expensesByCategory).length > 0) {
        financesSection += "\nExpenses by Category:\n";
        Object.entries(expensesByCategory).forEach(([category, amount]) => {
          financesSection += `- ${category}: ${formatCurrency(amount)}\n`;
        });
      }
      
      formattedOutput.push(financesSection);
    } else if (data.finances.error) {
      formattedOutput.push(`FINANCES ERROR: ${data.finances.error}`);
    }
  }
  
  // Format order data if present
  if (data.orders) {
    if (Array.isArray(data.orders)) {
      let ordersSection = `ORDERS (${data.orders.length} total):\n`;
      
      // Calculate totals
      const totalRevenue = data.orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
      
      // Count by status
      const ordersByStatus = {};
      data.orders.forEach(order => {
        const status = order.status || 'Unknown';
        if (!ordersByStatus[status]) {
          ordersByStatus[status] = 0;
        }
        ordersByStatus[status]++;
      });
      
      ordersSection += `Total Orders: ${data.orders.length}\n`;
      ordersSection += `Total Order Value: ${formatCurrency(totalRevenue)}\n`;
      ordersSection += `Average Order Value: ${formatCurrency(totalRevenue / (data.orders.length || 1))}\n`;
      
      // Add order status breakdown
      ordersSection += "\nOrders by Status:\n";
      Object.entries(ordersByStatus).forEach(([status, count]) => {
        ordersSection += `- ${status}: ${count}\n`;
      });
      
      // Add recent orders
      ordersSection += "\nRecent Orders:\n";
      data.orders.slice(0, 5).forEach(order => {
        const date = order.createdAt instanceof Date 
          ? format(order.createdAt, 'yyyy-MM-dd')
          : 'Unknown date';
        
        ordersSection += `- ${date}: ${formatCurrency(order.totalAmount || 0)} (${order.status || 'No status'})\n`;
      });
      
      formattedOutput.push(ordersSection);
    } else if (data.orders.error) {
      formattedOutput.push(`ORDERS ERROR: ${data.orders.error}`);
    }
  }
  
  // Format employee data if present
  if (data.employees) {
    if (Array.isArray(data.employees)) {
      let employeesSection = `EMPLOYEES (${data.employees.length} total):\n`;
      
      // Group by department/role
      const employeesByDept = {};
      data.employees.forEach(employee => {
        const dept = employee.department || 'No department';
        if (!employeesByDept[dept]) {
          employeesByDept[dept] = 0;
        }
        employeesByDept[dept]++;
      });
      
      employeesSection += `Total Employees: ${data.employees.length}\n`;
      
      if (Object.keys(employeesByDept).length > 0) {
        employeesSection += "\nEmployees by Department:\n";
        Object.entries(employeesByDept).forEach(([dept, count]) => {
          employeesSection += `- ${dept}: ${count}\n`;
        });
      }
      
      formattedOutput.push(employeesSection);
    } else if (data.employees.error) {
      formattedOutput.push(`EMPLOYEES ERROR: ${data.employees.error}`);
    }
  }
  
  // Format marketing data if present
  if (data.marketing) {
    if (Array.isArray(data.marketing)) {
      let marketingSection = `MARKETING (${data.marketing.length} campaigns/activities):\n`;
      
      // Group by campaign type
      const campaignsByType = {};
      data.marketing.forEach(campaign => {
        const type = campaign.type || 'Other';
        if (!campaignsByType[type]) {
          campaignsByType[type] = 0;
        }
        campaignsByType[type]++;
      });
      
      if (Object.keys(campaignsByType).length > 0) {
        marketingSection += "Marketing Activities by Type:\n";
        Object.entries(campaignsByType).forEach(([type, count]) => {
          marketingSection += `- ${type}: ${count}\n`;
        });
      }
      
      // Add recent marketing campaigns
      marketingSection += "\nRecent Marketing Activities:\n";
      data.marketing.slice(0, 5).forEach(campaign => {
        const date = campaign.date instanceof Date 
          ? format(campaign.date, 'yyyy-MM-dd')
          : 'Unknown date';
        
        marketingSection += `- ${date}: ${campaign.name || 'Unnamed'} (${campaign.type || 'No type'})\n`;
      });
      
      formattedOutput.push(marketingSection);
    } else if (data.marketing.error) {
      formattedOutput.push(`MARKETING ERROR: ${data.marketing.error}`);
    }
  }
  
  // Format business details if present
  if (data.business) {
    if (data.business.id) {
      let businessSection = "BUSINESS DETAILS:\n";
      
      businessSection += `Name: ${data.business.name || 'Unknown'}\n`;
      businessSection += `Type: ${data.business.type || 'Unknown'}\n`;
      businessSection += `Industry: ${data.business.industry || 'Unknown'}\n`;
      
      if (data.business.category) {
        businessSection += `Category: ${data.business.category}\n`;
      }
      
      if (data.business.address) {
        businessSection += `Location: ${data.business.address}\n`;
      }
      
      formattedOutput.push(businessSection);
    } else if (data.business.error) {
      formattedOutput.push(`BUSINESS ERROR: ${data.business.error}`);
    }
  }
  
  return formattedOutput.join('\n\n');
};

// Helper function to calculate total stock for an item
const getTotalStock = (item) => {
  if (!item) return 0;
  
  if (item.type === 'digital' || item.type === 'service') {
    return '∞';
  }
  
  let total = 0;
  Object.values(item.locations || {}).forEach(qty => {
    total += qty;
  });
  
  return total;
};

// Helper function to format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(value);
};